import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import Icon from '../icons'
import { apiSend, useAction } from '../api/hooks'
import type { FolderSummary } from '../api/types'
import { useLibraryFolders } from '../api/folders'
import { compareLibraryFolders, folderAncestors, folderLocation } from '../folder-navigation'
import { can } from '../api/permissions'
import { Loading } from '../components/ui'
import FolderManagementMenu from './FolderManagementMenu'
import {folderManagementActions} from './folder-management'

interface FolderNode {
  id: string
  label: string
  parentId: string | null
  rowVersion: number
  systemOwned: boolean
  children: FolderNode[]
}

function buildTree(rows: FolderSummary[]): FolderNode[] {
  const nodes = new Map<string, FolderNode>([...rows].sort(compareLibraryFolders).map((row): [string, FolderNode] => [row.id, {
    id: row.id, label: row.name, parentId: row.parent_id, rowVersion: row.row_version,
    systemOwned: row.system_owned, children: [],
  }]))
  const roots: FolderNode[] = []
  for (const node of nodes.values()) {
    const parent = node.parentId ? nodes.get(node.parentId) : undefined
    if (parent) parent.children.push(node)
    else roots.push(node)
  }
  return roots
}

function flattenOptions(nodes: FolderNode[], depth = 0): Array<{ node: FolderNode; depth: number }> {
  return nodes.flatMap((node) => [{ node, depth }, ...flattenOptions(node.children, depth + 1)])
}

function descendantIds(node: FolderNode): Set<string> {
  return new Set(flattenOptions(node.children).map(({ node: child }) => child.id))
}

export default function LibraryFolderTree({ roles }: { roles: string[] }) {
  const navigate = useNavigate()
  const location = useLocation()
  const canView = can(roles, 'folder.view') && can(roles, 'asset.view')
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())
  const [notice, setNotice] = useState('')
  const [moving, setMoving] = useState<FolderNode | null>(null)
  const [moveTarget, setMoveTarget] = useState('')
  const { run, pending } = useAction()
  const folders = useLibraryFolders(canView)

  // 目录树只展示服务端真实数据。接口尚未返回或失败时不能用示例目录，
  // 否则用户会误以为这些目录真实存在并在错误目录下创建材料。
  const nodes = useMemo(() => buildTree(folders.data?.data ?? []), [folders.data])
  const selected = new URLSearchParams(location.search).get('folder') ?? 'all'
  const folderReady = folders.data !== null
  useEffect(() => {
    const path = folderAncestors(folders.data?.data ?? [], selected)
    setExpanded(current => {
      const next = new Set(current)
      path.forEach(folder => next.add(folder.id))
      return next.size === current.size ? current : next
    })
  }, [selected, folders.data])

  if (!canView) return null

  const select = (id: string, label?: string) => {
    if (id !== 'all') setExpanded(current => new Set([...current, id]))
    navigate(folderLocation(id, label))
    setNotice('')
  }

  const reload = async () => { window.dispatchEvent(new Event('kb:folders-changed')) }
  const addFolder = (parentId?: string | null) => run(async () => {
    if (!folderReady) { setNotice('目录数据尚未加载，暂不能新建目录。'); return }
    const name = window.prompt('新建目录名称')?.trim() ?? ''
    if (!name) return
    await apiSend('POST', '/folders', { name, parent_id: parentId === undefined ? (selected === 'all' ? null : selected) : parentId })
    setNotice(`目录“${name}”已创建`)
    await reload()
  })
  const renameFolder = (node: FolderNode) => run(async () => {
    const name = window.prompt('修改目录名称', node.label)?.trim() ?? ''
    if (!name || name === node.label) return
    await apiSend('PATCH', `/folders/${encodeURIComponent(node.id)}`, { name, expected_version: node.rowVersion })
    setNotice(`目录已重命名为“${name}”`)
    await reload()
  })
  const openMove = (node: FolderNode) => {
    setMoving(node)
    setMoveTarget(node.parentId ?? '')
    setNotice('')
  }
  const moveFolder = () => run(async () => {
    if (!moving || moveTarget === moving.id || descendantIds(moving).has(moveTarget)) return
    await apiSend('POST', `/folders/${encodeURIComponent(moving.id)}/move`, { parent_id: moveTarget || null, expected_version: moving.rowVersion })
    setNotice(`目录“${moving.label}”已移动`)
    setMoving(null)
    await reload()
  })
  const deleteFolder = (node: FolderNode) => run(async () => {
    if (!window.confirm(`确认删除目录“${node.label}”？仅允许删除空目录。`)) return
    await apiSend('DELETE', `/folders/${encodeURIComponent(node.id)}`, { expected_version: node.rowVersion })
    if (selected === node.id) select('all')
    setNotice(`目录“${node.label}”已删除`)
    await reload()
  })

  const toggle = (id: string) => setExpanded((current) => {
    const next = new Set(current)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    return next
  })

  const renderNode = (node: FolderNode, depth = 0): JSX.Element => {
    const hasChildren = node.children.length > 0
    const isOpen = expanded.has(node.id)
    return (
      <div key={node.id} className="library-tree-node">
        <div className={`library-tree-row${selected === node.id ? ' is-selected' : ''}`} style={{ paddingLeft: 8 + Math.min(depth * 14,70) }}>
          {hasChildren ? <button type="button" className="tree-toggle" aria-label={`${isOpen ? '收起' : '展开'}${node.label}`} aria-expanded={isOpen} onClick={() => toggle(node.id)}><span aria-hidden="true">{isOpen ? '▾' : '▸'}</span></button> : <span className="tree-toggle-spacer" aria-hidden="true" />}
          <button type="button" className="tree-item" title={node.label} onClick={() => select(node.id, node.label)} aria-current={selected === node.id ? 'page' : undefined}>
            <Icon name="folder" size={15} /><span className="tree-label">{node.label}</span>
          </button>
          {folderReady ? <FolderManagementMenu name={node.label} actions={folderManagementActions(roles,node.systemOwned)} disabled={pending} onAction={action=>{
            if(action==='create')void addFolder(node.id)
            else if(action==='rename')void renameFolder(node)
            else if(action==='move')openMove(node)
            else if(action==='delete')void deleteFolder(node)
          }}/> : null}
        </div>
        {hasChildren && isOpen ? node.children.map((child) => renderNode(child, depth + 1)) : null}
      </div>
    )
  }

  return (
    <section className="library-tree" aria-label="售前知识库资料">
      {folders.error ? <div className="library-tree-error" role="alert">目录加载失败：{folders.error}</div> : null}
      <div className="library-tree-list">
        <div className={`library-tree-row tree-root-row${selected === 'all' ? ' is-selected' : ''}`}>
          <button type="button" className="tree-item tree-root" onClick={() => select('all')} aria-current={selected === 'all' ? 'page' : undefined}><Icon name="folder" size={15} /><span className="tree-label">全部资料</span></button>
          {folderReady ? <FolderManagementMenu name="全部资料" actions={folderManagementActions(roles,false,true)} disabled={pending} onAction={()=>void addFolder(null)}/> : null}
        </div>
        {folders.loading ? <Loading rows={5} /> : nodes.length > 0 ? nodes.map((node) => renderNode(node)) : <div className="library-tree-empty">暂无目录，可在根目录新建文件夹</div>}
      </div>
      {notice ? <div className="library-tree-notice" role="status">{notice}</div> : null}
      {moving ? <div className="library-tree-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setMoving(null) }}>
        <div className="library-tree-modal" role="dialog" aria-modal="true" aria-labelledby="move-folder-title">
          <div className="library-tree-modal-head"><div><h2 id="move-folder-title">移动文件夹</h2><p>将“{moving.label}”移动到目标位置</p></div><button type="button" className="tree-modal-close" aria-label="关闭移动窗口" onClick={() => setMoving(null)}>×</button></div>
          <label className="field"><span className="field-label">目标文件夹</span><select value={moveTarget} onChange={(event) => setMoveTarget(event.target.value)}>
            <option value="">根目录（全部资料）</option>
            {flattenOptions(nodes).map(({ node, depth }) => <option key={node.id} value={node.id} disabled={node.id === moving.id || descendantIds(moving).has(node.id)}>{'　'.repeat(depth)}{node.label}</option>)}
          </select></label>
          <div className="library-tree-modal-foot"><button type="button" className="btn" onClick={() => setMoving(null)}>取消</button><button type="button" className="btn primary" onClick={() => void moveFolder()} disabled={moveTarget === moving.id || descendantIds(moving).has(moveTarget)}>确认移动</button></div>
        </div>
      </div> : null}
    </section>
  )
}
